import { NextSeo } from 'next-seo';
import Head from 'next/head';

import { AppConfig } from '../utils/AppConfig';

type IMetaProps = {
  title: string;
  description: string;
  canonical?: string;
  ampURL?: string | null;
  hrefLang?: Array<{ link: string; lang: string }>;
  favicon?: string;
  keywords?: string;
  noindex?: boolean;
  nofollow?: boolean;
  isItr?: boolean;
  isOgEnabled?: boolean;
  img?: string;
};

const Meta = ({
  title,
  description,
  canonical,
  ampURL,
  hrefLang,
  favicon = 'https://assets1.cleartax-cdn.com/cleartax/images/1644906212_clearfavicon.png',
  keywords = '',
  noindex = false,
  nofollow = false,
  isItr = false,
  isOgEnabled = false,
  img = 'https://assets.clear.in/cleartax/images/1753522640_cleartaxhomepage_.png',
}: IMetaProps): JSX.Element => {
  const hrefLangTags =
    hrefLang &&
    hrefLang.map((tag) => (
      <link
        rel="alternate"
        key={tag.lang}
        href={tag.link}
        hrefLang={tag.lang}
      />
    ));

  return (
    <>
      <Head>
        <meta charSet="UTF-8" key="charset" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1"
          key="viewport"
        />
        <link rel="apple-touch-icon" href={favicon} key="apple" />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={favicon}
          key="icon32"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={favicon}
          key="icon16"
        />
        <link rel="icon" href={favicon} key="favicon" />
        {hrefLangTags}
        {ampURL && <link rel="amphtml" href={ampURL}></link>};
        {keywords ? <meta property="keywords" content={keywords} /> : null}
        {isItr ? (
          <>
            <meta name="author" content="ClearTax" />
            <meta
              name="copyright"
              content="Defmacro Software Pvt. Ltd. Copyright (c) 2020"
            />
            <meta
              name="viewport"
              content="initial-scale=1.0, maximum-scale=1.0, shrink-to-fit=no"
            />
            <meta
              name="google-site-verification"
              content="QRKBEdtz1fiPAGB6su7gSD9W9Bf2XnHN2VOGKgrETWE"
            />
            <meta
              property="og:image:alt"
              content="ITR Filing for FY 2022-23 (AY 2023-24): e-Filing of Income Tax Returns online made easy with ClearTax to get maximum tax refund. Income Tax login, upload Form-16, view/download Form 26AS, review your TDS deductions & track refund status for Income Tax Filing in India."
            />
          </>
        ) : null}
        {isOgEnabled && (
          <>
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={img} />
            <meta name="twitter:url" content={canonical} />
          </>
        )}
      </Head>
      <NextSeo
        title={title}
        description={description}
        canonical={canonical}
        openGraph={{
          locale: AppConfig.locale,
          site_name: AppConfig.site_name,
          url: canonical,
          type: 'website',
          title: title,
          description: description,
          ...(img
            ? {
                images: [
                  {
                    url: img,
                    width: 1200,
                    height: 630,
                  },
                ],
              }
            : {}),
        }}
        twitter={{
          cardType: 'summary_large_image',
          site: '@ClearTax',
        }}
        {...(noindex ? { noindex: noindex } : {})}
        {...(nofollow ? { nofollow: nofollow } : {})}
      />
    </>
  );
};

export { Meta };
